import React from 'react';
import { RightPanel } from './RightPanel';
import { Clock, User, Bot, DollarSign, Activity, Info } from 'lucide-react';
import { ConversationCost } from '../types';
import { AppCard } from './base/AppCard';
import { AppBadge } from './base/AppBadge';
interface ConversationCostDetailPanelProps {
  conversation: ConversationCost | null;
  isOpen: boolean;
  onClose: () => void;
}
export function ConversationCostDetailPanel({
  conversation,
  isOpen,
  onClose
}: ConversationCostDetailPanelProps) {
  if (!conversation) return null;
  const formatTimestamp = (timestamp: string) => {
    return new Date(timestamp).toLocaleString('it-IT', {
      day: '2-digit',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };
  const totalTokens = conversation.apiCalls.reduce((sum, call) => sum + call.tokens, 0);
  return <RightPanel isOpen={isOpen} onClose={onClose} title="Dettaglio costi conversazione" subtitle={`${formatTimestamp(conversation.timestamp)} • ${conversation.userLabel}`} width="lg" data-id="element-102">
      <div className="p-6 space-y-6" data-id="element-103">
        {/* Header Info */}
        <div className="flex items-center justify-between pb-4 border-b border-gray-100 dark:border-dark-subtle" data-id="element-104">
          <div className="space-y-2" data-id="element-105">
            <div className="flex items-center gap-4" data-id="element-106">
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400" data-id="element-107">
                <Bot className="w-4 h-4 mr-2" data-id="element-108" />
                {conversation.agentName}
              </div>
              <div className="flex items-center text-sm text-gray-600 dark:text-gray-400" data-id="element-109">
                <User className="w-4 h-4 mr-2" data-id="element-110" />
                {conversation.userLabel}
              </div>
            </div>
            <div className="flex items-center text-sm text-gray-600 dark:text-gray-400" data-id="element-111">
              <Clock className="w-4 h-4 mr-2" data-id="element-112" />
              {new Date(conversation.timestamp).toLocaleTimeString('it-IT', {
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            })}
            </div>
          </div>
          <div className="text-right" data-id="element-113">
            <div className="flex items-center gap-2 justify-end mb-1" data-id="element-114">
              <DollarSign className="w-5 h-5 text-green-600 dark:text-green-400" data-id="element-115" />
              <span className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-116">
                €{conversation.costEur.toFixed(3)}
              </span>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-117">
              Costo totale
            </p>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 gap-4" data-id="element-118">
          <AppCard className="bg-blue-50/30 dark:bg-blue-900/10" data-id="element-119">
            <div className="flex items-center gap-3" data-id="element-120">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg" data-id="element-121">
                <Activity className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-122" />
              </div>
              <div data-id="element-123">
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-124">
                  {conversation.apiCalls.length}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-125">
                  Chiamate API
                </p>
              </div>
            </div>
          </AppCard>

          <AppCard className="bg-purple-50/30 dark:bg-purple-900/10" data-id="element-126">
            <div className="flex items-center gap-3" data-id="element-127">
              <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg" data-id="element-128">
                <Info className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-129" />
              </div>
              <div data-id="element-130">
                <p className="text-2xl font-semibold text-gray-900 dark:text-gray-100" data-id="element-131">
                  {totalTokens.toLocaleString()}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400" data-id="element-132">
                  Token totali
                </p>
              </div>
            </div>
          </AppCard>
        </div>

        {/* API Calls Timeline */}
        <div data-id="element-133">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center" data-id="element-134">
            <Activity className="w-4 h-4 mr-2 text-blue-600 dark:text-blue-400" data-id="element-135" />
            Chiamate API
          </h3>

          <div className="relative" data-id="element-136">
            {/* Timeline line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-gray-200 dark:bg-dark-medium" data-id="element-137" />

            <div className="space-y-3" data-id="element-138">
              {conversation.apiCalls.map((call, idx) => <div key={call.id} className="relative flex gap-4" data-id="element-139">
                  {/* Timeline dot */}
                  <div className="relative z-10 flex-shrink-0 w-8 h-8 rounded-full bg-white dark:bg-dark-surface border-2 border-blue-500 dark:border-blue-400 flex items-center justify-center" data-id="element-140">
                    <div className="w-2 h-2 rounded-full bg-blue-500 dark:bg-blue-400" data-id="element-141" />
                  </div>

                  {/* Call content */}
                  <div className="flex-1 pb-3" data-id="element-142">
                    <AppCard className="bg-gray-50 dark:bg-dark-subtle" data-id="element-143">
                      <div className="flex items-start justify-between mb-2" data-id="element-144">
                        <div className="flex-1" data-id="element-145">
                          <div className="flex items-center gap-2 mb-1" data-id="element-146">
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-147">
                              {call.operation}
                            </span>
                            <AppBadge variant="blue" data-id="element-148">#{idx + 1}</AppBadge>
                          </div>
                          <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400" data-id="element-149">
                            <span className="font-mono" data-id="element-150">{call.timestamp}</span>
                            <span data-id="element-151">•</span>
                            <span data-id="element-152">{call.tokens.toLocaleString()} tokens</span>
                          </div>
                        </div>
                        <div className="text-right" data-id="element-153">
                          <span className="text-lg font-semibold text-gray-900 dark:text-gray-100" data-id="element-154">
                            €{call.costEur.toFixed(3)}
                          </span>
                        </div>
                      </div>

                      {/* Cost breakdown bar */}
                      <div className="mt-3" data-id="element-155">
                        <div className="h-1.5 bg-gray-200 dark:bg-dark-medium rounded-full overflow-hidden" data-id="element-156">
                          <div className="h-full bg-blue-500 dark:bg-blue-600 rounded-full transition-all duration-500" style={{
                        width: `${call.costEur / conversation.costEur * 100}%`
                      }} data-id="element-157" />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1" data-id="element-158">
                          {(call.costEur / conversation.costEur * 100).toFixed(1)}
                          % del costo totale
                        </p>
                      </div>
                    </AppCard>
                  </div>
                </div>)}
            </div>
          </div>
        </div>

        {/* Cost Breakdown */}
        <div data-id="element-159">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4" data-id="element-160">
            Ripartizione costi
          </h3>

          <AppCard className="bg-gray-50 dark:bg-dark-subtle" data-id="element-161">
            <div className="space-y-3" data-id="element-162">
              {conversation.apiCalls.map((call, idx) => <div key={call.id} className="flex items-center justify-between py-2 border-b border-gray-200 dark:border-dark-medium last:border-0" data-id="element-163">
                  <div className="flex items-center gap-3" data-id="element-164">
                    <span className="text-xs font-mono text-gray-500 dark:text-gray-400 w-6" data-id="element-165">
                      #{idx + 1}
                    </span>
                    <span className="text-sm text-gray-700 dark:text-gray-300" data-id="element-166">
                      {call.operation}
                    </span>
                  </div>
                  <div className="flex items-center gap-4" data-id="element-167">
                    <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-168">
                      {call.tokens} tok
                    </span>
                    <span className="text-sm font-mono font-medium text-gray-900 dark:text-gray-100 w-20 text-right" data-id="element-169">
                      €{call.costEur.toFixed(3)}
                    </span>
                  </div>
                </div>)}

              <div className="flex items-center justify-between pt-3 border-t-2 border-gray-300 dark:border-dark-medium" data-id="element-170">
                <span className="text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-171">
                  Totale
                </span>
                <div className="flex items-center gap-4" data-id="element-172">
                  <span className="text-xs text-gray-500 dark:text-gray-400" data-id="element-173">
                    {totalTokens} tok
                  </span>
                  <span className="text-base font-mono font-bold text-gray-900 dark:text-gray-100 w-20 text-right" data-id="element-174">
                    €{conversation.costEur.toFixed(3)}
                  </span>
                </div>
              </div>
            </div>
          </AppCard>
        </div>

        {/* Metadata */}
        <div data-id="element-175">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-4" data-id="element-176">
            Metadati
          </h3>

          <AppCard className="bg-gray-50 dark:bg-dark-subtle" data-id="element-177">
            <div className="grid grid-cols-2 gap-4 text-sm" data-id="element-178">
              <div data-id="element-179">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-180">
                  ID Conversazione
                </p>
                <p className="font-mono text-gray-900 dark:text-gray-100" data-id="element-181">
                  {conversation.id}
                </p>
              </div>
              <div data-id="element-182">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-183">
                  User ID
                </p>
                <p className="font-mono text-gray-900 dark:text-gray-100" data-id="element-184">
                  {conversation.userId}
                </p>
              </div>
              <div data-id="element-185">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-186">
                  Costo medio per chiamata
                </p>
                <p className="font-mono text-gray-900 dark:text-gray-100" data-id="element-187">
                  €
                  {(conversation.costEur / conversation.apiCalls.length).toFixed(3)}
                </p>
              </div>
              <div data-id="element-188">
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-1" data-id="element-189">
                  Token medi per chiamata
                </p>
                <p className="font-mono text-gray-900 dark:text-gray-100" data-id="element-190">
                  {Math.round(totalTokens / conversation.apiCalls.length)}
                </p>
              </div>
            </div>
          </AppCard>
        </div>
      </div>
    </RightPanel>;
}